import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService, WidgetService, FunnelStepService, CampaignService } from '../_services';
import { CrudSavePopupComponent } from '../_directives';
import { FormBuilder, Validators } from '@angular/forms';
import 'grapesjs-preset-webpage/dist/grapesjs-preset-webpage.min';
import { takeUntil } from 'rxjs/operators';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Widget, ContentBlockType, Campaign, WidgetVariable, getFunnelPathVariables } from '../_models';
import * as tinymce from 'tinymce';

@Component({
  moduleId: module.id.toString(),
  selector: 'widget-v2-form',
  templateUrl: './widget-v2-edit.component.html',
  styleUrls: ['./widget-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class Widgetv2FormComponent extends CrudSavePopupComponent implements OnInit {
  campaign: Campaign;
  stepList: any[];
  showEditors = false;
  widgets: Widget[] = [];
  types: { value: string; label: string }[] = [
    { value: ContentBlockType.PrivacyPolicy, label: 'Privacy Policy' },
    { value: ContentBlockType.ReturnPolicy, label: 'Return Policy' },
    { value: ContentBlockType.ReturnAddress, label: 'Return Address' },
    { value: ContentBlockType.TermsAndConditions, label: 'Terms and Conditions' },
    { value: ContentBlockType.OfferTerms, label: 'Offer Terms' },
    { value: ContentBlockType.Custom, label: 'Custom' },
  ];
  allowContentLinks = true;
  contentType = ContentBlockType;
  private staticTypes = [
    ContentBlockType.PrivacyPolicy,
    ContentBlockType.ReturnPolicy,
    ContentBlockType.ReturnAddress,
    ContentBlockType.TermsAndConditions,
    ContentBlockType.OfferTerms,
  ];
  funnelPathVariables: { value: string; text: string }[] = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    protected widgetService: WidgetService,
    protected alertService: AlertService,
    protected modalService: NgxSmartModalService,
    protected stepService: FunnelStepService,
    protected campaignService: CampaignService
  ) {
    super(router, location, route, widgetService, alertService);
    this.objectName = 'Widget';
  }

  ngOnInit() {
    this.setForm(
      this.formBuilder.group({
        type: [null, Validators.required],
        name: [null, Validators.required],
        content: [null],
        enhanced_mode: [false],
        enhanced_content: [null],
        enhanced_content_components: [null],
      })
    );

    if (this.inputData.length > 0) {
      this.widgets = [...this.inputData];
      this.inputData = this.widgets[0];
    }

    this.campaign = this.campaignService.getCurrentCampaign();

    super.ngOnInit();
    this.stepService
      .getGlobalJumpLinks(false, { resourcetype: 'VisualFunnel' })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (stepList) => {
          this.stepList = stepList;
          this.showEditors = true;
          this.loading = false;
        },
        (error) => {
          this.showEditors = true;
          this.loading = false;
          this.handleError(error);
        }
      );

    this.widgetService
      .getVariables()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (widgets: WidgetVariable[]) => {
          let items = getFunnelPathVariables();

          widgets.forEach((widget: WidgetVariable) => {
            items.push({ value: '{' + widget.slug + '}', text: widget.name });
          });

          const allowedValues = [
            '{brand_name}',
            '{email}',
            '{first_name}',
            '{full_name}',
            '{customer_id}',
            '{last_name}',
            '{phone_number}',
            '{order_create_date}',
            '{order_id}',
            '{support_email}',
            '{support_phone}'
          ];

          items = items.filter(item => allowedValues.includes(item.value));

          items.sort((a, b): number => {
            if (a.text < b.text) {
              return -1;
            }
            if (a.text > b.text) {
              return 1;
            }
            return 0;
          });

          this.funnelPathVariables = items;
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  protected onInputDataChanged() {
    if (this.inputData) {
      if (this.types.some((type) => type.value === this.inputData.slug)) {
        this.form.patchValue({ type: this.inputData.slug });
        this.onTypeChanged(this.inputData.slug);
      } else {
        this.form.patchValue({ type: 'custom' });
        this.onTypeChanged('custom');
      }
    }
  }

  private onTypeChanged(newType: string) {
    this.allowContentLinks = this.staticTypes.indexOf(newType as ContentBlockType) === -1;
  }

  selectType(event) {
    const newType = event.target.value as ContentBlockType;

    if (newType === ContentBlockType.Custom) {
      this.form.patchValue({ name: null });
    } else {
      this.form.patchValue({ name: this.types.find((type) => type.value === newType).label });
    }

    this.onTypeChanged(newType);
  }

  preview() {
    this.loading = true;
    this.widgetService.preview(this.campaign.id, this.getFormData()).subscribe(
      (data: string) => {
        window.open(data, '_blank');
        this.loading = false;
      },
      (error) => {
        this.handleError(error);
        this.loading = false;
      }
    );
  }

  onCloseEditDialog() {
    this.loading = false;
  }

  protected getFormData(): any {
    let data = Object.assign(this.form.value);

    if (data.type !== ContentBlockType.Custom) {
      const foundType = this.types.find((type) => type.value === data.type);
      data.name = foundType ? foundType.label : '';
    }

    delete data['type']; // remove type from data - it's a virtual field

    this.widgets.forEach((widget: Widget) => {
      Object.assign(widget, data);
    });

    if (this.widgets.length > 1) {
      return this.widgets;
    }

    return this.widgets.length > 0 ? this.widgets[0] : {};
  }

  onSubmit() {
    if (this.isNew && this.widgets.length > 1) {
      this.submitted = true;

      if (this.form.valid) {
        this.loading = true;
        this.widgetService.bulkCreate(this.getFormData()).subscribe(
          (data) => {
            if (this.showSuccessMessage) {
              this.alertService.success('Objects created successfully.', true);
            }

            this.canDeactivateService.setFormDirty(false);
            this.onSaveComplete(data);
          },
          (error) => {
            this.handleSubmitError(error);
            this.loading = false;
          }
        );
      }
    } else {
      super.onSubmit();
    }
  }

  addVariable(event: any) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = event.target.value;
    const editorInstance = tinymce.get('content-' + this.formId);

    if (editorInstance) {
      editorInstance.insertContent(selectedValue);
    }

    selectElement.selectedIndex = 0;
  }
}
